import React from 'react';
import { Box, Typography, Avatar, useTheme, Divider } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LogoutIcon from '@mui/icons-material/Logout';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import OutlineButton from './Buttons/OutlineButton';
import { useLanguage } from '../hooks/useLanguage';
import { getNavSectionsForScope } from '../config/adminScope';
import { useAdminScope } from '../context/AdminScopeContext';

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string) || '';
const LOGIN_ORIGIN = (import.meta.env.VITE_LOGIN_ORIGIN as string) || 'http://login.localtest.me:3000';
const APP_ORIGIN = (import.meta.env.VITE_APP_ORIGIN as string) || 'http://app.localtest.me:3001';

const compactBtnSx = {
  margin: '0.35em auto',
  padding: '0.55em 1.2em',
  fontSize: '0.92em',
};

const AdminSidebar: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { navigateWithLang, getPathWithoutLang, currentLanguage } = useLanguage();
  const location = useLocation();
  const { scope, displayName: scopeDisplayName, email: scopeEmail } = useAdminScope();
  const navSections = getNavSectionsForScope(scope);

  const currentPath = getPathWithoutLang(location.pathname);

  const displayName = scopeDisplayName;
  const email = scopeEmail;

  const isActive = (path: string) => {
    if (path === '/') return currentPath === '/' || currentPath === '';
    return currentPath === path || currentPath.startsWith(`${path}/`);
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/v1/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch {
      /* ignore */
    }
    window.location.replace(`${LOGIN_ORIGIN.replace(/\/$/, '')}/`);
  };

  const renderNavButton = (path: string, labelKey: string, Icon: React.ElementType, comingSoon?: boolean) => {
    const active = isActive(path);
    return (
      <OutlineButton
        key={path}
        type="button"
        fullWidth
        disabled={comingSoon}
        onClick={() => !comingSoon && navigateWithLang(path)}
        sx={{
          ...compactBtnSx,
          justifyContent: 'flex-start',
          ...(comingSoon
            ? {
                backgroundColor: (theme.palette as any).blue?.lightTransparent || 'rgba(128, 173, 230, 0.12)',
                opacity: 0.85,
              }
            : {}),
          ...(active && !comingSoon
            ? {
                backgroundColor: (theme.palette as any).blue?.lightTransparent || 'rgba(128, 173, 230, 0.17)',
                borderColor: theme.palette.primary.main,
              }
            : {}),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <Icon
              sx={{
                color: active || comingSoon ? theme.palette.primary.main : 'inherit',
                fontSize: 20,
                flexShrink: 0,
              }}
            />
            <Typography
              noWrap
              sx={{
                color: active || comingSoon ? theme.palette.primary.main : 'inherit',
                fontSize: '0.95em',
              }}
            >
              {t(labelKey)}
            </Typography>
          </Box>
          {comingSoon ? (
            <Typography variant="caption" sx={{ color: theme.palette.primary.main, fontStyle: 'italic', ml: 0.5 }}>
              …
            </Typography>
          ) : null}
        </Box>
      </OutlineButton>
    );
  };

  return (
    <Box display="flex" flexDirection="column" pb={2}>
      <Box textAlign="center" mb={2}>
        <Avatar
          sx={{
            width: 64,
            height: 64,
            mx: 'auto',
            mb: 1,
            bgcolor: theme.palette.primary.main,
            fontSize: '1.35rem',
          }}
        >
          {(displayName || 'A').charAt(0).toUpperCase()}
        </Avatar>
        {displayName ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            {displayName}
          </Typography>
        ) : null}
        <Typography variant="caption" color="primary" sx={{ fontWeight: 'medium', display: 'block' }}>
          {scope === 'full' ? t('sidebar.superAdmin') : t('sidebar.adminRole')}
        </Typography>
        {email ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25, wordBreak: 'break-all' }}>
            {email}
          </Typography>
        ) : null}
      </Box>

      {navSections.map((section, idx) => (
        <Box key={section.sectionKey} mb={0.5}>
          {idx > 0 ? <Divider sx={{ my: 1 }} /> : null}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', px: 0.5, mb: 0.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            {t(section.sectionKey)}
          </Typography>
          {section.items.map(({ path, labelKey, icon, comingSoon }) =>
            renderNavButton(path, labelKey, icon, comingSoon)
          )}
        </Box>
      ))}

      <Divider sx={{ my: 1 }} />
      {navSections.some((s) => s.sectionKey === 'sidebar.sections.system') ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', px: 0.5, mb: 0.5, fontWeight: 600, textTransform: 'uppercase' }}
        >
          {t('sidebar.sections.system')}
        </Typography>
      ) : null}

      <OutlineButton
        type="button"
        fullWidth
        onClick={() => {
          const base = APP_ORIGIN.replace(/\/$/, '');
          window.location.assign(`${base}/${currentLanguage}/home`);
        }}
        sx={{ ...compactBtnSx, justifyContent: 'flex-start' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <OpenInNewIcon sx={{ fontSize: 20 }} />
          <Typography fontSize="0.95em">{t('sidebar.openApp')}</Typography>
        </Box>
      </OutlineButton>

      <OutlineButton type="button" fullWidth onClick={handleLogout} sx={{ ...compactBtnSx, justifyContent: 'flex-start' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <LogoutIcon sx={{ fontSize: 20 }} />
          <Typography fontSize="0.95em">{t('sidebar.logout')}</Typography>
        </Box>
      </OutlineButton>
    </Box>
  );
};

export default AdminSidebar;
